import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { OptionSchema } from "@schema-types/types/JourneySchema";
import { CircleCheck } from "lucide-react";
import React from "react";
import { cn } from "@shadcn/lib/utils";

// Based this input on https://www.shadcnui-blocks.com/components/radio-group

interface RadioCardsProps {
  options: OptionSchema[];
  defaultValue?: string;
  value?: string;
  disabled?: boolean;
  onValueChange?: (value: string) => void;
}

const RadioGroupNew = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root
      className={cn("grid w-full max-w-sm grid-cols-3 gap-3", className)}
      {...props}
      ref={ref}
    />
  );
});

RadioGroupNew.displayName = RadioGroupPrimitive.Root.displayName;

const RadioGroupItemNew = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        "relative rounded px-4 py-2 text-start ring-[1px] ring-border disabled:opacity-50 data-[state=checked]:ring-2 data-[state=checked]:ring-primary",
        className
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="absolute right-2 top-2">
        <CircleCheck className="fill-primary text-primary-foreground" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
});
RadioGroupItemNew.displayName = RadioGroupPrimitive.Item.displayName;

export { RadioGroupNew, RadioGroupItemNew as RadioGroupItem };

const RadioCards = ({ options, defaultValue, value, disabled, onValueChange }: RadioCardsProps) => {
  return (
    <RadioGroupPrimitive.Root
      defaultValue={defaultValue}
      value={value}
      className="grid w-full max-w-sm grid-cols-3 gap-3"
      onValueChange={onValueChange}
    >
      {options.map((option) => (
        <RadioGroupPrimitive.Item
          key={option.value}
          value={option.value}
          disabled={disabled}
          className="relative flex flex-col rounded px-4 py-3 min-h-[2.5rem] text-sm text-start ring-[1px] ring-border disabled:opacity-50 data-[state=checked]:ring-2 data-[state=checked]:ring-ring"
        >
          <span className="tracking-tight pr-8">{option.label}</span>

          <RadioGroupPrimitive.Indicator className="absolute right-2 top-2">
            <CircleCheck className="fill-primary text-primary-foreground" />
          </RadioGroupPrimitive.Indicator>
        </RadioGroupPrimitive.Item>
      ))}
    </RadioGroupPrimitive.Root>
  );
};

export default RadioCards;
